import { Provider } from 'react-redux';
import React, { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { isNil } from './utils/fp';
import CssBaseline from '@mui/material/CssBaseline';
import { getLocale } from './selectors/router';
import configureStore, { history } from './configureStore';
import { FormattedMessage, I18nProvider, initialize as initializeI18n } from './i18n';
import { initialize as initializeAnalytics } from './utils/analytics';
import initializeOidc from './oidc/init.js';
import { ThemeProvider } from './theme';
import searchApi from './api/search';
import shareApi from './api/share';
import { search, i18n, theme } from './lib/settings';
import ErrorBoundary from './components/error-boundary';
import Helmet from './components/helmet';
import meta from '../../package.json';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Route, Routes } from 'react-router';
import App from './components/app';
import CloudflareChallenge from './components/cloudflare-challenge';
import HistoryRouter from './components/history-router';
import { getBasename } from './utils/index.js';
import { Loader } from './components/vis/loader.jsx';
import { ROUTE_SEARCH, ROUTE_SHARE, ROUTE_VIEWER, ROUTE_VIS } from './constants.js';
import { browserTracingIntegration, init } from '@sentry/react';

console.info(`${meta.name}@${meta.version}`); // eslint-disable-line no-console

const Search = lazy(() => import('./components/search/search.jsx'));
const Vis = lazy(() => import('./components/vis.jsx'));
const Share = lazy(() => import('./share/share.jsx'));
const NotFound = lazy(() => import('./components/not-found.jsx'));
const Viewer = lazy(() => import('./viewer/components/index.jsx'));

const initSentry = () => {
  if (isNil(window.CONFIG.sentryDSN)) return;

  init({
    dsn: window.CONFIG.sentryDSN,
    environment: window.CONFIG.sentryEnv,
    release: window.CONFIG.sentryRelease,
    integrations: [browserTracingIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    enableErrorHandling: false,
    autoSessionTracking: false,
  });
};

const initQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: false,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      },
    },
    queryCache: new QueryCache({
      onSuccess: (data, query) => {
        if (typeof query?.meta?.successHandler !== 'function') return;
        query.meta.successHandler({ data, queryKey: query.meta.queryKey });
      },
      onError: (error, query) => {
        if (typeof query?.meta?.errorHandler !== 'function') return;
        /* if (error.code === 'E_JSON_PARSE' && isNil(spaceId)) {
          // redirect to invalid page with proper arg (ie invalid space)
          // when router will be full setup
        }*/
        query.meta.errorHandler({ error, queryKey: query.meta.queryKey });
        /*if (error?.response?.status === 401 && hasExternalAuth)
          dispatch(failedExtAuth(spaceId)); */
      },
    }),
  });
};

const run = async () => {
  const initialState = {};

  initSentry();
  const queryClient = initQueryClient();
  const store = configureStore(initialState);
  const locale = getLocale(store.getState());

  searchApi.setConfig(search);
  shareApi.setConfig(window.SETTINGS?.share);
  initializeI18n(i18n, locale);
  await initializeOidc({ store });
  initializeAnalytics({ locale });

  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <ErrorBoundary id="root">
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <I18nProvider messages={window.I18N}>
              <Helmet />
              <CssBaseline />
              <QueryClientProvider client={queryClient}>
                <HistoryRouter history={history} basename={getBasename()}>
                  <App>
                    <ErrorBoundary id="app">
                      <Suspense
                        fallback={
                          <Loader
                            message={<FormattedMessage id="de.route.loading" />}
                            styles={{ marginTop: 176, marginRight: 0 }}
                          />
                        }
                      >
                        <Routes>
                          <Route path={ROUTE_SEARCH} element={<Search />} />
                          <Route path={ROUTE_VIS} element={<Vis />} />
                          <Route path={ROUTE_SHARE} element={<Share />} />
                          <Route path={ROUTE_VIEWER} element={<Viewer />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      <CloudflareChallenge />
                      <ReactQueryDevtools initialIsOpen={true} />
                    </ErrorBoundary>
                  </App>
                </HistoryRouter>
              </QueryClientProvider>
            </I18nProvider>
          </ThemeProvider>
        </Provider>
      </ErrorBoundary>
    </StrictMode>,
  );
};

run();
